import React, { useState } from 'react';
import { Loading, SortableGallery, Toasts, Upload } from '@fourthwall/components';
import { PlusIcon } from '@fourthwall/icons';
import { base64ToFile, fileToBase64 } from '@fourthwall/utils/dist/file';
import { preloadImage } from '@fourthwall/utils/dist/preloadImage';
import { GalleryImageUploader } from '../../../components/ImageUpload/GalleryImageUploader';
import { renderEmptyStateUploadZone } from './components/EmptyState/EmptyStateUpdateZone';
import { OfferImagesFilters } from './components/Filters/OfferImagesFilters';
import { useOfferGallery } from './hooks/useOfferGallery';
import { useOfferImagesFilters } from './hooks/useOfferImagesFilters';
import { useUploadImages } from './hooks/useUpload/useUploadImages';
import * as S from './styled';
const mapIndexPosition = (item, index) => {
    return { ...item, position: index };
};
export const OfferImages = ({ offerVariantTypes, value = [], disabled = false, onChange, onColorFilterSort, onUploadStart, onUploadEnd, }) => {
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [droppedImages, setDroppedImages] = useState([]);
    const filters = useOfferImagesFilters(offerVariantTypes);
    const { uploadBlobs } = useUploadImages({
        filters,
    });
    const gallery = useOfferGallery({
        filters,
        value,
        onChange,
    });
    const hasMultipleFilterOptions = filters.some((filter) => filter.options.length > 1);
    const handleUpload = async (blobs) => {
        onUploadStart();
        await uploadBlobs.mutateAsync(blobs, {
            onSuccess: (uploadedFiles) => {
                onUploadEnd();
                onChange([...value, ...uploadedFiles].map(mapIndexPosition));
            },
            onError: () => {
                Toasts.notify("We couldn't upload your photos", { type: 'error' });
                onUploadEnd();
            },
        });
    };
    const handleImageAdd = async (acceptedFiles) => {
        if (!acceptedFiles || !acceptedFiles.length)
            return;
        try {
            const droppedImages = await Promise.all(acceptedFiles.map(async (file) => {
                const base64 = await fileToBase64(file);
                return [base64, file.type];
            }));
            // Don't open Upload Modal if all images have the default aspect ratio and there is no multiple filter options, like:
            // Color filter with 2 options, Size filter with 3 options
            if (!hasMultipleFilterOptions) {
                const allImagesHasDefaultAspectRatio = await Promise.all(droppedImages.map(async (image) => {
                    const img = new Image();
                    img.src = image[0];
                    await preloadImage(image[0]);
                    return img.naturalWidth / img.naturalHeight === 3 / 4;
                }))
                    .then((images) => images.every((image) => image))
                    .catch(() => false);
                if (allImagesHasDefaultAspectRatio) {
                    const blobs = await Promise.all(droppedImages.map(async ([base64, type]) => {
                        const file = await base64ToFile(base64, 'image', type);
                        return { blob: file, uploadToAllVariants: false };
                    }));
                    await handleUpload(blobs);
                    return;
                }
            }
            setDroppedImages(droppedImages);
            setIsUploadModalOpen(true);
        }
        catch (error) {
            Toasts.notify("We couldn't upload your photos", { type: 'error' });
        }
    };
    const handleImageAddError = (rejectedFiles) => {
        rejectedFiles?.forEach(({ file, errors }) => {
            if (errors.length > 0) {
                Toasts.notify(`${file.name}: ${errors[0].message}`, { type: 'error' });
            }
        });
    };
    return (React.createElement(S.Container, null,
        filters.length > 0 && (React.createElement(OfferImagesFilters, { filters: filters, onColorFilterSort: onColorFilterSort })),
        React.createElement("div", null,
            React.createElement(Loading, { isLoading: uploadBlobs.isLoading },
                React.createElement(SortableGallery, { data: gallery.data, inactiveStartIndex: 12, uploadZone: !disabled &&
                        (gallery.data.length > 0 ? (React.createElement(Upload, { title: "", multiple: true, accept: {
                                'image/*': [],
                            }, icon: PlusIcon, iconSize: 24, onFileDrop: handleImageAdd, onFileDropRejected: handleImageAddError })) : (React.createElement(Upload, { content: renderEmptyStateUploadZone, multiple: true, orientation: "vertical", accept: {
                                'image/*': [],
                            }, onFileDrop: handleImageAdd, onFileDropRejected: handleImageAddError }))), onSortChange: gallery.onImagesSort, onView: gallery.onImageView, onRemove: gallery.onImageRemove }))),
        isUploadModalOpen && (React.createElement(GalleryImageUploader, { images: droppedImages, uploadToAllVariantsVisible: hasMultipleFilterOptions, onClose: () => {
                setDroppedImages([]);
                setIsUploadModalOpen(false);
            }, uploadBlobs: handleUpload }))));
};
